/**
 * Categorical hues: six colours beside the brand blue for *categories*
 * (a reason for calling) and *identity* (a caller's avatar). Status stays
 * semantic and is always a dot beside a word.
 *
 * A hue is stable — a category is mapped explicitly (`shared/intent.ts`)
 * and an identity is hashed (`hueFor`) — and it appears as a thin bar, a
 * dot, or a tint, never as a saturated fill behind body text.
 */
export type Hue =
  | 'accent'
  | 'teal'
  | 'violet'
  | 'rose'
  | 'amber'
  | 'sky'
  | 'lime';

export const HUES: Hue[] = [
  'accent',
  'teal',
  'violet',
  'rose',
  'amber',
  'sky',
  'lime',
];

/** Solid fill for thin bars and dots. */
export const HUE_FILL: Record<Hue, string> = {
  accent: 'bg-v2-accent',
  teal: 'bg-v2-teal',
  violet: 'bg-v2-violet',
  rose: 'bg-v2-rose',
  amber: 'bg-v2-amber',
  sky: 'bg-v2-sky',
  lime: 'bg-v2-lime',
};

/** Hue text, for a selected label that carries a hue. */
export const HUE_TEXT: Record<Hue, string> = {
  accent: 'text-v2-accent',
  teal: 'text-v2-teal',
  violet: 'text-v2-violet',
  rose: 'text-v2-rose',
  amber: 'text-v2-amber',
  sky: 'text-v2-sky',
  lime: 'text-v2-lime',
};

/**
 * Tinted chip: soft background, hue text. Strong enough to read across a
 * long list (the hospitality pills), still light enough that 11px hue text
 * stays legible in both themes.
 */
export const HUE_TINT: Record<Hue, string> = {
  accent: 'bg-v2-accent/[0.16] text-v2-accent',
  teal: 'bg-v2-teal/[0.18] text-v2-teal',
  violet: 'bg-v2-violet/[0.18] text-v2-violet',
  rose: 'bg-v2-rose/[0.18] text-v2-rose',
  amber: 'bg-v2-amber/[0.20] text-v2-amber',
  sky: 'bg-v2-sky/[0.18] text-v2-sky',
  lime: 'bg-v2-lime/[0.20] text-v2-lime',
};

/** Hover for a `HUE_TINT` control: the same hue, a deeper tint. */
export const HUE_TINT_HOVER: Record<Hue, string> = {
  accent: 'hover:bg-v2-accent/[0.28]',
  teal: 'hover:bg-v2-teal/[0.30]',
  violet: 'hover:bg-v2-violet/[0.30]',
  rose: 'hover:bg-v2-rose/[0.30]',
  amber: 'hover:bg-v2-amber/[0.32]',
  sky: 'hover:bg-v2-sky/[0.30]',
  lime: 'hover:bg-v2-lime/[0.32]',
};

/** Wash for a tile that carries a hue (figure tiles, summary facts). */
export const HUE_WASH: Record<Hue, string> = {
  accent: 'bg-v2-accent/[0.09]',
  teal: 'bg-v2-teal/[0.09]',
  violet: 'bg-v2-violet/[0.09]',
  rose: 'bg-v2-rose/[0.09]',
  amber: 'bg-v2-amber/[0.11]',
  sky: 'bg-v2-sky/[0.09]',
  lime: 'bg-v2-lime/[0.11]',
};

export const isHue = (value: unknown): value is Hue =>
  typeof value === 'string' && (HUES as string[]).includes(value);

/**
 * Stable hue for an identity (customer id, phone). Skips `accent` so a
 * person never looks like Arthur or a link.
 */
export function hueFor(identity: string | number | null | undefined): Hue {
  const s = String(identity ?? '');
  if (!s) return 'sky';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const pool = HUES.filter((x) => x !== 'accent');
  return pool[h % pool.length];
}
